(() => {
  const runtimeKey = '__phoxIlluminationText';
  if (window[runtimeKey]) {
    window[runtimeKey].refresh();
    return;
  }

  const controllerSelector = 'script[data-phox-illumination-controller]';
  const choiceAttribute = 'data-phox-illumination-choice';
  const kindAttribute = 'data-phox-illumination-kind';
  const coveredAttribute = 'data-phox-illumination-covered';
  const textOnlyAttribute = 'data-phox-illumination-text-only';
  const markerAttributes = [choiceAttribute, kindAttribute, coveredAttribute, textOnlyAttribute];
  const menuSelector = [
    '.header__inline-menu .header__menu-item',
    '.header__inline-menu .mega-menu__link',
    '.menu-drawer__navigation .menu-drawer__menu-item',
    '.menu-drawer__navigation .menu-drawer__close-button'
  ].join(',');
  const heroSelector = [
    '.phox-hero__heading',
    '.phox-hero__reveal-heading',
    '.phox-den__copy > h1',
    '.phox-cart-hero__title',
    '.slideshow .banner__heading'
  ].map((selector) => `#MainContent ${selector}`).join(',');
  const sectionSelector = [
    '.phox-loadout__title',
    '.phox-spotlight__title',
    '.phox-steam__heading',
    '.phox-editorial__heading',
    '.phox-ceo__heading',
    '.phox-scrollstory__heading',
    '.phox-performance__header > h2',
    '.phox-den__lookbook > h2',
    '.phox-cart-recommendations__title',
    '.phox-product-profile__heading',
    '.phox-clean-product__display-title',
    '.product__title > h1',
    '.product__title > a > h2',
    'h2.product__title',
    '.main-page-title',
    '.collection-hero__title',
    '.main-blog > .title--primary',
    '.article-template__title',
    '.phox-story .pn-story-top h1',
    '.phox-story .pn-body h2',
    '.related-products__heading',
    '.blog__title',
    '.collection__title > .title',
    '.title-wrapper-with-link > .title',
    '.rich-text__heading',
    '.image-with-text__heading',
    '.collapsible-content__heading',
    '.collage-wrapper-title',
    '.newsletter__wrapper > h2',
    '.video-section .title',
    '.contact > .title',
    '.template-404 > .title',
    '.template-search__header > h1',
    '#phox-news .pn-title',
    '#phox-news .pn-feature h2'
  ].map((selector) => `#MainContent ${selector}`).join(',');
  const cardSelector = '#MainContent #phox-news .pn-card h3';
  const headingSelector = 'h1,h2,h3,h4,h5,h6,[role="heading"]';
  const candidateSelector = [
    menuSelector, heroSelector, sectionSelector, cardSelector, headingSelector,
    'p', 'a[href]', 'button', 'summary', '[role="button"]'
  ].join(',');
  const protectedSelector = [
    'script', 'style', 'template', 'noscript', 'svg', '.svg-wrapper',
    '.visually-hidden', '.sr-only', '.screen-reader-text',
    '.header__heading', '.header__heading-link', '.header__heading-logo-wrapper',
    '.header__heading-logo', '.phox-hero__reveal-logo',
    '[data-phox-illumination-ignore]'
  ].join(',');
  const ignoredTextSelector = [
    protectedSelector, 'img', 'picture', 'video', 'audio', 'canvas', 'object',
    'iframe', '.icon', '[aria-hidden="true"]'
  ].join(',');
  const mediaSelector = 'svg,img,picture,video,canvas,object,iframe,.svg-wrapper,[style*="background-image"]';
  const observedAttributes = [
    'data-illuminate-text', 'data-unlit-text', 'data-phox-illumination-controller',
    'data-phox-illumination-mode', 'data-phox-illumination-menu',
    'data-phox-illumination-hero', 'data-phox-illumination-sections'
  ];
  const ranks = { hero: 4, section: 3, card: 2, menu: 2, text: 1 };
  const entries = new Map();
  const dirtyElements = new Set();
  let needsFullScan = true;
  let scheduled = false;
  let started = false;
  let body;

  const normalize = (value) => String(value || '')
    .normalize('NFKC')
    .replace(/[\u2018\u2019\u02bc\uff07]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/[\s.!?…,:;·•|—–›»→↗↘↖↙]+$/u, '')
    .trim()
    .toLowerCase();

  const parseList = (value) => new Set(String(value || '')
    .split(/[\r\n\u2028\u2029]+/u)
    .map(normalize)
    .filter(Boolean));

  const clearMarkers = (element) => {
    markerAttributes.forEach((attribute) => element.removeAttribute(attribute));
  };

  const readLabel = (element) => {
    const copy = element.cloneNode(true);
    copy.querySelectorAll(ignoredTextSelector).forEach((node) => node.remove());
    copy.querySelectorAll('br').forEach((node) => node.replaceWith(' '));
    return normalize(copy.textContent);
  };

  const classify = (element) => {
    if (element.matches(menuSelector)) return { kind: 'menu', automatic: 'menu' };
    if (element.matches(heroSelector)) return { kind: 'hero', automatic: 'hero' };
    if (element.matches(cardSelector)) return { kind: 'card', automatic: 'sections' };
    if (element.matches(sectionSelector)) return { kind: 'section', automatic: 'sections' };
    if (element.matches(headingSelector)) return { kind: 'section', automatic: null };
    return { kind: 'text', automatic: null };
  };

  const remember = (element) => {
    if (!element.isConnected || !element.matches(candidateSelector) || element.closest(protectedSelector)) {
      clearMarkers(element);
      entries.delete(element);
      return;
    }
    const label = readLabel(element);
    if (!label) {
      clearMarkers(element);
      entries.delete(element);
      return;
    }
    entries.set(element, { ...classify(element), label });
  };

  const queueAncestors = (node) => {
    let element = node.nodeType === 1 ? node : node.parentElement;
    while (element && element !== body) {
      if (element.matches(candidateSelector) || entries.has(element)) dirtyElements.add(element);
      element = element.parentElement;
    }
  };

  const queueTree = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches(candidateSelector)) dirtyElements.add(node);
    node.querySelectorAll(candidateSelector).forEach((element) => dirtyElements.add(element));
  };

  const hasMedia = (element) => {
    if (element.querySelector(mediaSelector)) return true;
    if (typeof window.getComputedStyle !== 'function') return false;
    const backgroundImage = window.getComputedStyle(element).backgroundImage;
    return Boolean(backgroundImage && backgroundImage !== 'none');
  };

  const reconcile = () => {
    scheduled = false;
    if (!body || !body.isConnected) return;
    if (needsFullScan) {
      body.querySelectorAll(candidateSelector).forEach(remember);
      needsFullScan = false;
      dirtyElements.clear();
    } else {
      dirtyElements.forEach(remember);
      dirtyElements.clear();
    }
    entries.forEach((entry, element) => {
      if (!element.isConnected) {
        clearMarkers(element);
        entries.delete(element);
      }
    });

    const controllers = document.querySelectorAll(controllerSelector);
    const controller = controllers[controllers.length - 1];
    const illuminate = parseList(controller?.getAttribute('data-illuminate-text'));
    const unlit = parseList(controller?.getAttribute('data-unlit-text'));
    const automaticMode = body.getAttribute('data-phox-illumination-mode') !== 'chosen';
    const enabled = body.hasAttribute('data-phox-illumination');
    const decisions = new Map();

    entries.forEach((entry, element) => {
      const choice = unlit.has(entry.label) ? 'off' : illuminate.has(entry.label) ? 'on' : null;
      const automatic = automaticMode && entry.automatic && body.getAttribute(`data-phox-illumination-${entry.automatic}`) === 'true';
      decisions.set(element, {
        ...entry,
        choice,
        active: Boolean(enabled && choice !== 'off' && (choice === 'on' || automatic)),
        covered: false
      });
    });

    decisions.forEach((decision, element) => {
      if (decision.choice !== 'off') return;
      let ancestor = element.parentElement;
      while (ancestor && ancestor !== body) {
        const parent = decisions.get(ancestor);
        if (parent) parent.covered = true;
        ancestor = ancestor.parentElement;
      }
    });

    decisions.forEach((decision, element) => {
      let ancestor = element.parentElement;
      while (ancestor && ancestor !== body) {
        const parent = decisions.get(ancestor);
        if (parent?.choice === 'off') decision.covered = true;
        ancestor = ancestor.parentElement;
      }
    });

    decisions.forEach((decision, element) => {
      if (!decision.active || decision.covered) return;
      let ancestor = element.parentElement;
      while (ancestor && ancestor !== body) {
        const parent = decisions.get(ancestor);
        if (parent?.active && !parent.covered) {
          if (parent.label === decision.label && ranks[decision.kind] > ranks[parent.kind]) {
            parent.covered = true;
          } else {
            decision.covered = true;
            break;
          }
        }
        ancestor = ancestor.parentElement;
      }
    });

    decisions.forEach((decision, element) => {
      if (decision.choice) element.setAttribute(choiceAttribute, decision.choice);
      else element.removeAttribute(choiceAttribute);
      if (decision.choice === 'on') element.setAttribute(kindAttribute, decision.kind);
      else element.removeAttribute(kindAttribute);
      element.toggleAttribute(coveredAttribute, decision.covered);
      const textOnly = decision.active && !decision.covered && (decision.kind === 'hero' || decision.kind === 'section') && hasMedia(element);
      element.toggleAttribute(textOnlyAttribute, textOnly);
    });
    body.removeAttribute('data-phox-illumination-pending');
  };

  const schedule = () => {
    if (!started || scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(reconcile);
  };

  const refresh = () => {
    needsFullScan = true;
    schedule();
  };

  const start = () => {
    if (started) return;
    body = document.body;
    if (!body) return;
    started = true;
    reconcile();
    const observer = new MutationObserver((records) => {
      let relevant = false;
      records.forEach((record) => {
        if (record.type === 'attributes') {
          relevant = true;
          return;
        }
        if (record.type === 'characterData') {
          queueAncestors(record.target);
          relevant = relevant || dirtyElements.size > 0;
          return;
        }
        queueAncestors(record.target);
        record.addedNodes.forEach((node) => {
          queueTree(node);
          if (node.nodeType === 1 && (node.matches(controllerSelector) || node.querySelector(controllerSelector))) relevant = true;
        });
        relevant = relevant || dirtyElements.size > 0 || record.removedNodes.length > 0;
      });
      if (relevant) schedule();
    });
    observer.observe(body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: observedAttributes });
    const headObserver = new MutationObserver(schedule);
    if (document.head) headObserver.observe(document.head, { subtree: true, attributes: true, attributeFilter: ['data-illuminate-text', 'data-unlit-text'], childList: true });
    document.addEventListener('shopify:section:load', (event) => {
      queueTree(event.target);
      queueAncestors(event.target);
      schedule();
    });
    window.addEventListener('pageshow', refresh);
  };

  window[runtimeKey] = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
